import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ICheckDuplicateAccountPayload,
  ICheckDuplicateUserPayload,
  ICheckVatPayload,
  ISignup,
  ISignupConfirmPhone,
  ISignupPhone,
  ISignupUser,
} from '@app/models/signup';
import { BehaviorSubject, Observable } from 'rxjs';
import { CaptchaHandlingService } from '@app/shared/services/captcha-handling/captcha-handling.service';
import { CustomHttpParams } from '@infomaniak/ngx-helpers';

@Injectable({
  providedIn: 'root',
})
export class SignupService {
  loading = new BehaviorSubject<boolean>(false);
  public isCreatingMyKSuite$ = new BehaviorSubject<boolean>(false);
  public confirmPhoneCode: ISignupConfirmPhone = null;

  constructor(private http: HttpClient, private captchaHandling: CaptchaHandlingService) {
    this._hasAccountDuplicated = new BehaviorSubject<boolean>(false);
    this._hasUserDuplicated = new BehaviorSubject<boolean>(false);
    this._isEmailExists = new BehaviorSubject<boolean>(false);
    this._isConfirmCodeValidated = new BehaviorSubject<boolean>(false);
  }

  private _hasAccountDuplicated: BehaviorSubject<boolean>;

  get hasAccountDuplicated(): Observable<boolean> {
    return this._hasAccountDuplicated.asObservable();
  }

  private _hasUserDuplicated: BehaviorSubject<boolean>;

  get hasUserDuplicated(): Observable<boolean> {
    return this._hasUserDuplicated.asObservable();
  }

  private _isConfirmCodeValidated: BehaviorSubject<boolean>;

  get isConfirmCodeValidated(): Observable<boolean> {
    return this._isConfirmCodeValidated.asObservable();
  }

  private _isEmailExists: BehaviorSubject<boolean>;

  get isEmailExists(): Observable<boolean> {
    return this._isEmailExists.asObservable();
  }

  private _hasAdditionalStreet = false;

  get hasAdditionalStreet() {
    return this._hasAdditionalStreet;
  }

  // public get confirmPhoneCode(): string {
  //   return this._confirmPhoneCode;
  // }
  // public set confirmPhoneCode(value: string) {
  //   this._confirmPhoneCode = value;
  // }

  set hasAdditionalStreet(bool: boolean) {
    this._hasAdditionalStreet = bool;
  }

  deepTrim(obj) {
    const ret = {};
    for (const prop in obj) {
      if (prop) {
        const value = obj[prop],
          type = typeof value;
        if (
          value != null &&
          (type === 'string' || type === 'object' || type === 'number' || type === 'boolean') &&
          obj.hasOwnProperty(prop)
        ) {
          if (type === 'number') {
            ret[prop] = obj[prop];
          } else if (type === 'object') {
            ret[prop] = this.deepTrim(obj[prop]);
          } else if (type === 'string') {
            ret[prop] = prop === 'password' ? obj[prop] : obj[prop].trim();
          } else if (type === 'boolean') {
            ret[prop] = obj[prop] ? 1 : 0;
          } else {
            ret[prop] = obj[prop];
          }
        }
      }
    }
    return ret;
  }

  public setIsEmailExists(newValue: boolean) {
    setTimeout(() => {
      this._isEmailExists.next(newValue);
    });
  }

  public setHasAccountDuplicated(newValue: boolean) {
    setTimeout(() => {
      this._hasAccountDuplicated.next(newValue);
    });
  }

  public setHasUserDuplicated(newValue: boolean) {
    setTimeout(() => {
      this._hasUserDuplicated.next(newValue);
    });
  }

  public setConfirmCodeValidation(newValue: boolean) {
    setTimeout(() => {
      this._isConfirmCodeValidated.next(newValue);
    });
  }

  checkEmail(email: string): Promise<any> {
    const uri = '/api/register/check_email';
    const payload = { email: email };

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload);
        },
        payload,
        true
      )
      .toPromise()
      .catch((error: Error) => {
        throw new Error(`CheckEmail::113 -- ${error.message}`);
      });
  }

  checkMyKsuiteEmail(obj: { email: string; first_name: string; last_name: string }): Promise<any> {
    const uri = '/api/register/check_email';

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload, {
            params: new CustomHttpParams({ skipRedirect: true }),
          });
        },
        obj,
        true
      )
      .toPromise()
      .catch((error) => {
        console.error(`checkMyKsuiteEmail::120 -- ${error.message}`);
        throw error;
      });
  }

  checkPassword(obj: { password: string }): Promise<any> {
    return this.http.post('/api/components/password/check', JSON.stringify(obj)).toPromise();
  }

  checkBlacklistName(name: string): Promise<any> {
    const uri = '/api/register/check_blacklist_account';
    const payload = { account: { name } };

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload);
        },
        payload,
        true
      )
      .toPromise();
  }

  checkDuplicateAccount(payload: ICheckDuplicateAccountPayload) {
    const uri = '/api/register/check_duplicate_account';

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload);
        },
        payload,
        true
      )
      .toPromise();
  }

  checkDuplicateUser(payload: ICheckDuplicateUserPayload): Promise<any> {
    const uri = '/api/register/check_duplicate_user';

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload);
        },
        payload,
        true
      )
      .toPromise();
  }

  checkVat(vatNumber) {
    return this.http.post<ICheckVatPayload>(
      '/api/invoicing/vat/number/verify',
      JSON.stringify({ vat_number: vatNumber })
    );
  }

  createUserOnly(payload: ISignupUser, cgu_acceptance = true, mailing_acceptance = false): Promise<any> {
    const uri = '/api/register/user';
    const params = JSON.stringify({
      user: { ...this.deepTrim(payload) },
      cgu_acceptance: cgu_acceptance,
      mailing_acceptance: mailing_acceptance,
    });

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload, {
            params: new CustomHttpParams({ skipRedirect: true }),
          });
        },
        params,
        true
      )
      .toPromise();
  }

  createAccountOnly(payload: ISignup, force?: boolean, challenge?: string): Promise<any> {
    const uri = '/api/register/account' + (force ? '?bypass=true' : '');
    const params = JSON.stringify({
      account: { ...this.deepTrim(payload.account) },
      user: { ...this.deepTrim(payload.user) },
      cgu_acceptance: payload.cgu_acceptance,
      mailing_acceptance: payload.mailing_acceptance,
      'ik-shield-challenge-id': challenge,
    });

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload, {
            params: new CustomHttpParams({ skipRedirect: true }),
          });
        },
        params,
        true
      )
      .toPromise();
  }

  createIndividualAccount(payload: ISignup, force?: boolean, challenge?: string): Promise<any> {
    const uri = '/api/register/user_and_account' + (force ? '?bypass=true' : '');
    const params = { ...this.deepTrim(payload), 'ik-shield-challenge-id': challenge };

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload, {
            params: new CustomHttpParams({ skipRedirect: true }),
          });
        },
        params,
        true
      )
      .toPromise();
  }

  checkPhone(payload: ISignupPhone): Promise<any> {
    const uri = '/api/register/check_phone';

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload);
        },
        payload,
        true
      )
      .toPromise();
  }

  checkConfirmPhoneCode(payload: ISignupConfirmPhone): Promise<any> {
    const uri = '/api/register/confirm_phone_code';

    return this.captchaHandling
      .protectWithCaptcha(
        (newPayload) => {
          return this.http.post(uri, newPayload);
        },
        payload,
        true
      )
      .toPromise();
  }

  autoLogin(payload: { user: string; password: string; url: string }) {
    const body = new URLSearchParams();
    body.set('login', payload.user);
    body.set('password', payload.password);
    body.set('ajax', '1');
    body.set('r', '');
    return this.http
      .post(payload.url, body.toString(), {
        headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
        withCredentials: true,
      })
      .toPromise();
  }
}
